import { ALL_SHIP_IDS, getShipDefinition, type PlanetShips, type PlayerRace, type ShipId } from '@kaeliux/shared';

/** The explicitly-ranked head of the scrap order (issue #120): satellite, colony ship, spy ship, recycler. */
const AUTO_UNLOCK_HEAD: ShipId[] = [13, 10, 12, 11];

/**
 * Ship-scrap priority order for the Planet Lock overlay's "Auto-unlock"
 * button (issue #120): satellites and civilian ships go first (least
 * militarily valuable), then war ships by ascending id (cheapest first).
 *
 * The tail is derived from `ALL_SHIP_IDS` rather than spelled out, so a
 * newly-added ship id is picked up automatically instead of being silently
 * skipped by Auto-unlock — `computeAutoUnlockSelection` can only ever clear
 * a deficit if every scrappable ship type is reachable from this list.
 */
export const AUTO_UNLOCK_PRIORITY: ShipId[] = [
    ...AUTO_UNLOCK_HEAD,
    ...ALL_SHIP_IDS.filter((id) => !AUTO_UNLOCK_HEAD.includes(id)),
];

/**
 * Greedily selects docked ships to scrap, in `AUTO_UNLOCK_PRIORITY` order,
 * until at least `deficit` population has been freed (or every docked ship
 * has been selected, if that's not enough). Never selects more of a given
 * ship type than is actually docked. The final ship type picked may free
 * slightly more than `deficit` — population is only freed in whole-ship
 * increments, so overshooting by up to that ship's `hangar` cost is
 * unavoidable when the remainder doesn't divide evenly.
 *
 * Because `AUTO_UNLOCK_PRIORITY` covers every `ShipId`, the selection always
 * covers a deficit the caller derived from the same docked fleet: the lock's
 * `deficit` is `dockedPopulation - capacity`, which can never exceed the
 * `dockedPopulation` selecting everything would free. A partial selection is
 * therefore only reachable by passing a `deficit` the fleet never produced.
 */
export function computeAutoUnlockSelection(
    race: PlayerRace,
    dockedShips: PlanetShips | undefined,
    deficit: number,
): Partial<Record<ShipId, number>> {
    const selection: Partial<Record<ShipId, number>> = {};
    let remaining = deficit;

    for (const shipId of AUTO_UNLOCK_PRIORITY) {
        if (remaining <= 0) break;
        const docked = dockedShips?.[shipId] ?? 0;
        if (docked <= 0) continue;
        const hangar = getShipDefinition(race, shipId).hangar;
        if (hangar <= 0) continue;

        const take = Math.min(docked, Math.ceil(remaining / hangar));
        selection[shipId] = take;
        remaining -= take * hangar;
    }

    return selection;
}
